import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import VLFCard from "@/components/Card";
import EmptyData from "@/components/EmptyData";

import {ScoutReportAPI} from "@/api/scout_report";
import {ScoutingReportAPI} from "@/api/scouting_report";
import {PlayerValuationAPI} from "@/api/player_valuation";
import {MatchAPI} from "@/api/match";
import {PlayerAPI} from "@/api/player";
import {ERROR_OPERATION} from "@/utils/constants";
import {useCategories} from "@/utils/hooks";
import {
	initialValuationParameters,
	initialBaseValuationParameters,
} from "@/utils/constants";

import "./index.scss";

function ScoutingDashboardPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const {categories} = useCategories({
		setLoading: false,
		handleNotification,
	});

	const [loading, setLoading] = useState(false);
	const [stats, setStats] = useState({
		totalReports: 0,
		totalValuations: 0,
		totalTryouts: 0,
		totalMatches: 0,
	});
	const [topProfiles, setTopProfiles] = useState([]);
	const [profilesByRole, setProfilesByRole] = useState({});
	const [profilesByCategory, setProfilesByCategory] = useState({});

	const season = useSelector((state) => state?.season);

	// Recupera tutte le statistiche
	const getStats = async () => {
		setLoading(true);
		try {
			const seasonStartDate = season?.startDate || moment().startOf("year").format("YYYY-MM-DD");
			const seasonEndDate = season?.endDate || moment().endOf("year").format("YYYY-MM-DD");

			// Recupera dati in parallelo
			const [reports, playerValuations, scoutingReports, tryouts, matches] = await Promise.all([
				// Segnalazioni da /GoalkeepersReports
				ScoutReportAPI.list({
					fromDate: seasonStartDate,
					toDate: seasonEndDate,
					seasonId,
				}),
				// Valutazioni da /PlayersValuations
				PlayerValuationAPI.list({
					fromDate: seasonStartDate,
					toDate: seasonEndDate,
					prospect: true,
					seasonId,
				}),
				// Valutazioni da /ScoutsReports
				ScoutingReportAPI.list({
					fromDate: seasonStartDate,
					toDate: seasonEndDate,
					seasonId,
				}),
				// Provini da /PlayersValuations con tryouts: true (stessi filtri della pagina provini)
				PlayerValuationAPI.list({
					fromDate: seasonStartDate,
					toDate: seasonEndDate,
					tryouts: true,
					seasonId,
				}),
				MatchAPI.list({
					fromDate: seasonStartDate,
					toDate: seasonEndDate,
					prospect: true,
					seasonId,
				}),
			]);

			// Totali
			const totalValuations = (playerValuations?.length || 0) + (scoutingReports?.length || 0);

			setStats({
				totalReports: reports?.length || 0,
				totalValuations: totalValuations,
				totalTryouts: tryouts?.length || 0,
				totalMatches: matches?.length || 0,
			});

			// Calcola migliori profili (combina valutazioni da entrambe le fonti)
			const allValuations = [
				...(playerValuations || []),
				...(scoutingReports || []),
			];
			await calculateTopProfiles(allValuations);
			
			// Calcola statistiche per ruolo e categoria
			await calculateProfilesStats();
		} catch (error) {
			console.error("Errore nel recupero delle statistiche:", error);
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// Calcola i migliori profili basati sulle valutazioni
	const calculateTopProfiles = async (valuations) => {
		try {
			// Raggruppa valutazioni per giocatore
			const playerValuationsMap = {};
			valuations?.forEach((v) => {
				const playerId = v.player?.id;
				if (playerId) {
					if (!playerValuationsMap[playerId]) {
						playerValuationsMap[playerId] = {
							player: v.player,
							valuations: [],
							totalRating: 0,
							count: 0,
						};
					}
					playerValuationsMap[playerId].valuations.push(v);
					// Usa finalAverage se disponibile, altrimenti prova altri campi
					const rating = v.finalAverage !== null && v.finalAverage !== undefined
						? parseFloat(v.finalAverage)
						: (v.finalOverallRating !== null && v.finalOverallRating !== undefined
							? parseFloat(v.finalOverallRating)
							: null);
					if (rating !== null && !isNaN(rating)) {
						playerValuationsMap[playerId].totalRating += rating;
						playerValuationsMap[playerId].count += 1;
					}
				}
			});

			// Calcola media per ogni giocatore e recupera parametri migliori
			const profilesWithStats = await Promise.all(
				Object.values(playerValuationsMap).map(async (pv) => {
					const avgRating = pv.count > 0 ? pv.totalRating / pv.count : 0;
					
					// Calcola parametri migliori dalle valutazioni già recuperate
					const bestParams = calculateBestParameters(pv.valuations, pv.player);
					
					return {
						...pv.player,
						averageRating: avgRating,
						totalValuations: pv.count,
						bestParameters: bestParams,
					};
				}),
			);

			// Ordina per rating medio e prendi i top 10
			const top = profilesWithStats
				.filter((p) => p.averageRating > 0)
				.sort((a, b) => b.averageRating - a.averageRating)
				.slice(0, 10);

			setTopProfiles(top);
		} catch (error) {
			console.error("Errore nel calcolo dei migliori profili:", error);
		}
	};

	// Calcola i parametri migliori di un giocatore
	const calculateBestParameters = (valuations, player) => {
		if (!valuations || !valuations.length) return [];

		const playerCategory = categories?.filter(
			(c) => c.id === player?.playerCategoryId,
		)?.[0];

		const parameters =
			!playerCategory?.basicActivities || player?.playerCategoryName === "Under 13"
				? initialValuationParameters(t)
				: initialBaseValuationParameters(t);

		const areaAverages = {};
		
		valuations.forEach((v) => {
			parameters.forEach((p) => {
				if (v[p.fieldKeyRating] !== null && v[p.fieldKeyRating] !== undefined) {
					if (!areaAverages[p.area]) {
						areaAverages[p.area] = {
							total: 0,
							count: 0,
							label: p.label,
						};
					}
					areaAverages[p.area].total += parseFloat(v[p.fieldKeyRating]);
					areaAverages[p.area].count += 1;
				}
			});
		});

		// Calcola medie e ordina
		const bestParams = Object.entries(areaAverages)
			.map(([area, data]) => ({
				area: area.charAt(0).toUpperCase() + area.slice(1),
				label: data.label,
				average: data.count > 0 ? data.total / data.count : 0,
			}))
			.filter((p) => p.average > 0)
			.sort((a, b) => b.average - a.average)
			.slice(0, 5); // Top 5 parametri

		return bestParams;
	};

	// Calcola statistiche per ruolo e categoria
	const calculateProfilesStats = async () => {
		try {
			const response = await PlayerAPI.paginated({
				page: 1,
				pageSize: 1000,
				prospect: true,
				seasonId,
			});

			const players = response.data || [];

			// Raggruppa per ruolo
			const byRole = {};
			players.forEach((p) => {
				const role = p.goalkeeper ? "Portiere" : p.primaryRole || "Non specificato";
				byRole[role] = (byRole[role] || 0) + 1;
			});

			// Raggruppa per categoria
			const byCategory = {};
			players.forEach((p) => {
				const category = p.playerCategory?.name || "Non specificato";
				byCategory[category] = (byCategory[category] || 0) + 1;
			});

			setProfilesByRole(byRole);
			setProfilesByCategory(byCategory);
		} catch (error) {
			console.error("Errore nel calcolo delle statistiche profili:", error);
		}
	};

	useEffect(() => {
		if (seasonId) {
			getStats();
		}
	}, [seasonId]);

	const renderStatCard = (title, value, icon) => (
		<div className="col-12 col-md-6 col-lg-3 padding-small">
			<VLFCard
				content={
					<div className="stat-card">
						<div className="stat-icon">{icon}</div>
						<div className="stat-content">
							<div className="stat-value">{value}</div>
							<div className="stat-label">{title}</div>
						</div>
					</div>
				}
			/>
		</div>
	);

	const renderTopProfiles = () => {
		if (!topProfiles.length) {
			return <EmptyData />;
		}

		return (
			<div className="top-profiles-list">
				{topProfiles.map((profile, index) => (
					<div
						key={profile.id}
						className="profile-item"
						onClick={() => navigate(`/scouting/players/${profile.id}`)}
					>
						<div className="profile-rank">#{index + 1}</div>
						<div className="profile-info">
							<div className="profile-name">
								{profile.firstName} {profile.lastName}
							</div>
							<div className="profile-details">
								{profile.playerCategory?.name && (
									<span className="profile-category">
										{profile.playerCategory.name}
									</span>
								)}
								{profile.goalkeeper ? (
									<span className="profile-role">Portiere</span>
								) : (
									profile.primaryRole && (
										<span className="profile-role">{profile.primaryRole}</span>
									)
								)}
							</div>
							{profile.bestParameters && profile.bestParameters.length > 0 && (
								<div className="profile-best-params">
									<strong>Parametri migliori:</strong>{" "}
									{profile.bestParameters
										.map((p) => `${p.label} (${p.average.toFixed(1)})`)
										.join(", ")}
								</div>
							)}
						</div>
						<div className="profile-rating">
							<div className="rating-value">
								{profile.averageRating.toFixed(2)}
							</div>
							<div className="rating-label">Media</div>
							<div className="rating-count">
								{profile.totalValuations} valutaz.
							</div>
						</div>
					</div>
				))}
			</div>
		);
	};

	const renderStatsByRole = () => {
		const entries = Object.entries(profilesByRole);
		if (!entries.length) return <EmptyData />;

		return (
			<div className="stats-grid">
				{entries.map(([role, count]) => (
					<div key={role} className="stat-item">
						<div className="stat-item-value">{count}</div>
						<div className="stat-item-label">{role}</div>
					</div>
				))}
			</div>
		);
	};

	const renderStatsByCategory = () => {
		const entries = Object.entries(profilesByCategory);
		if (!entries.length) return <EmptyData />;

		return (
			<div className="stats-grid">
				{entries.map(([category, count]) => (
					<div key={category} className="stat-item">
						<div className="stat-item-value">{count}</div>
						<div className="stat-item-label">{category}</div>
					</div>
				))}
			</div>
		);
	};

	return (
		<Page className="scouting-dashboard-page">
			<Loading visible={loading} light />
			<Breadcrumbs />
			<div className="row no-margin col-container">
				{/* Statistiche principali */}
				<div className="col-12 padding-small">
					<VLFCard
						title="Statistiche Stagione"
						content={
							<div className="row no-margin">
								{renderStatCard(
									"Totale Segnalazioni",
									stats.totalReports,
									"📋",
								)}
								{renderStatCard(
									"Totale Valutazioni",
									stats.totalValuations,
									"⭐",
								)}
								{renderStatCard("Totale Provini", stats.totalTryouts, "🎯")}
								{renderStatCard(
									"Partite Visionate",
									stats.totalMatches,
									"⚽",
								)}
							</div>
						}
					/>
				</div>

				{/* Migliori profili */}
				<div className="col-12 padding-small">
					<VLFCard
						title="Migliori Profili"
						content={renderTopProfiles()}
					/>
				</div>

				{/* Statistiche per ruolo */}
				<div className="col-12 col-md-6 padding-small">
					<VLFCard
						title="Profili per Ruolo"
						content={renderStatsByRole()}
					/>
				</div>

				{/* Statistiche per categoria */}
				<div className="col-12 col-md-6 padding-small">
					<VLFCard
						title="Profili per Categoria"
						content={renderStatsByCategory()}
					/>
				</div>
			</div>
		</Page>
	);
}

export default ScoutingDashboardPage;

